// «Service» på en ordre eller et skiplukk (OrderDetails): servicene varene hører til, f.eks. skiene
// etter «Gjør om til service». Koblingen går begge veier (Order.serviceIds ↔
// WorkshopService.butikkvarer, src/lib/butikkvarer.js). Varene vises da på servicefakturaen, mens
// verkstedstatistikken bare teller servicens egne linjer og deler.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { knappSekundar } from '../ny-kundesamhandling/Skjema';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
const dato = (d) => (d ? new Date(d).toLocaleDateString('nb-NO') : '');
const navn = (s) => [s.serviceRef && `#${s.serviceRef}`, s.tekst || s.kategori, s.status].filter(Boolean).join(' · ');

export default function OrdreServicer({ ordre, onOrdreEndret }) {
  const [data, setData] = useState(null);
  const [arbeider, setArbeider] = useState('');
  const [feil, setFeil] = useState('');
  const skiplukk = ordre.type === 'skiplukk';
  // Ordresiden trenger koblingene (OrdrePris tar ut verkstedarbeid når servicen har det).
  const melde = useRef(onOrdreEndret);
  melde.current = onOrdreEndret;

  const hent = useCallback(async () => {
    try {
      const r = await fetch(`${API_BASE_URL}/orders/${ordre._id}/servicer`);
      if (!r.ok) throw new Error();
      const b = await r.json();
      setData(b);
      melde.current?.({ serviceIds: (b.koblet || []).map((s) => s._id) });
    } catch {
      setFeil('Klarte ikke å hente servicene.');
    }
  }, [ordre._id]);

  useEffect(() => { hent(); }, [hent]);

  const endre = async (serviceId, koble) => {
    setArbeider(serviceId);
    setFeil('');
    try {
      const r = await fetch(`${API_BASE_URL}/workshop-services/${serviceId}/butikkvarer`, {
        method: koble ? 'PUT' : 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: koble ? JSON.stringify({ ordreId: ordre._id }) : undefined,
      });
      const b = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(b.message || 'Klarte ikke å lagre koblingen.');
      await hent();
    } catch (e) {
      setFeil(e.message);
    } finally {
      setArbeider('');
    }
  };

  if (!data && !feil) return null;
  const koblet = data?.koblet || [];
  // Servicer som allerede har andre varer koblet, vises ikke som forslag. Avsluttede og kansellerte
  // servicer kommer ikke med fra backend (GET /orders/:id/servicer).
  const kandidater = (data?.kandidater || []).filter((s) => !s.koblet);
  if (!koblet.length && !kandidater.length && !ordre.overfortTilService?.dato) return null;

  return (
    <section aria-labelledby="os-tittel" className="space-y-2 rounded-2xl border border-slate-200 bg-white p-3 sm:p-3">
      <h2 id="os-tittel" className="text-base font-bold text-slate-900">Service</h2>
      {koblet.length > 0 && (
        <ul className="space-y-2">
          {koblet.map((s) => (
            <li key={s._id} className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-1.5">
              <Link to={`/workshop/${s._id}`} className="min-w-0 font-semibold text-indigo-700 underline underline-offset-2 [overflow-wrap:anywhere]">
                Service {navn(s)}
              </Link>
              <button type="button" onClick={() => endre(s._id, false)} disabled={Boolean(arbeider)}
                className="text-sm font-semibold text-red-800 underline underline-offset-2 disabled:opacity-50">
                Fjern koblingen<span className="sr-only"> til service {s.serviceRef}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
      <p className="text-slate-800">
        {koblet.length
          ? `${skiplukk ? 'Skiene' : 'Varene'} vises samlet med servicen på servicefakturaen. Verkstedstatistikken teller bare arbeid og deler i selve servicen.`
          : skiplukk && ordre.overfortTilService?.dato
            ? 'Koble servicen som ble registrert i butikkmodus, så vises skiene og prepp samlet på servicefakturaen.'
            : 'Skal varene til service? Koble servicen, så vises alt samlet på servicefakturaen.'}
      </p>
      {kandidater.length > 0 && (
        <ul className="space-y-2">
          {kandidater.map((s) => (
            <li key={s._id} className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-slate-200 px-3 py-1.5">
              <span className="min-w-0 text-slate-800 [overflow-wrap:anywhere]">
                Service {navn(s)}{s.dato ? ` · registrert ${dato(s.dato)}` : ''}
              </span>
              <button type="button" onClick={() => endre(s._id, true)} disabled={Boolean(arbeider)} className={knappSekundar}>
                {arbeider === s._id ? 'Kobler …' : 'Koble til'}<span className="sr-only"> service {s.serviceRef}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
      {!koblet.length && !kandidater.length && (
        <p className="text-sm text-slate-700">{ordre.Status === 'Avsluttet' ? 'Ordren er avsluttet, så den kan ikke kobles til en service.' : 'Kunden har ingen aktive servicer ennå. Kom tilbake hit når servicen er registrert.'}</p>
      )}
      {feil && <p role="alert" className="text-sm font-medium text-red-800">{feil}</p>}
    </section>
  );
}
